import { createSignal, onMount, onCleanup, createEffect, Show, on, untrack, createMemo } from "solid-js";
import { useActiveManeuverViewMirror } from "../../../../store/activeManeuverViewStore";

import DropDownButton from "../../../../components/buttons/DropDownButton";

import DataTable_Big from "../../../../components/maneuvers/standard/DataTable_Big";
import DataTable_Small from "../../../../components/maneuvers/standard/DataTable_Small";
import Map from "../../../../components/maneuvers/standard/Map";
import Video from "../../../../components/maneuvers/standard/Video";
import Scatter from "../../../../components/maneuvers/standard/Scatter";
import TimeSeries, { MAP_SIDE_PANEL_EXTRA_HEIGHT_PX, MAP_SIDE_PANEL_BUTTON_GAP_PX } from "../../../../components/maneuvers/standard/TimeSeries";
import DataTable_BigGrouped from "../../../../components/maneuvers/grouped/DataTable_Big";
import DataTable_SmallGrouped from "../../../../components/maneuvers/grouped/DataTable_Small";
import MapGrouped from "../../../../components/maneuvers/grouped/Map";
import ScatterGrouped from "../../../../components/maneuvers/grouped/Scatter";
import TimeSeriesGrouped from "../../../../components/maneuvers/grouped/TimeSeries";
import ScatterTimeseries from "../../../../components/charts/ScatterTimeseries";

import { setupMediaContainerScaling } from "../../../../utils/global";
import { logPageLoad, logActivity, getCurrentProjectDatasetIds } from "../../../../utils/logging";
import { error as logError, warn as logWarning, debug as logDebug } from "../../../../utils/console";
import { useDashboardShareRestore, willRestoreShare, shareRestoreReady } from "../../../../hooks/useDashboardShareRestore";
import { useManeuverStatesForEventType } from "../../../../utils/maneuverStateReconcile";
import { buildManeuversShareState, applyManeuversShareState, currentManeuverBaselineShare, consumeShareSpecifiedBaseline } from "../../../../utils/reportShareState";

import { phase, setPhase, color, setColor, tws, setTws, eventType, setEventType, grouped, groupDisplayMode, setGroupDisplayMode, maneuvers, setManeuvers, setTableData, setFiltered, filtered, tabledata, hasVideoMenu, maneuverTableSort, resetManeuverTableSort } from "../../../../store/globalStore";
import { sortManeuverRows } from "../../../../utils/maneuverTableSort";
import { selectedEvents, setSelectedEvents, setLegendSelectionActive, setTriggerUpdate, setTriggerSelection, setSelection, selection, setHasSelection, hasSelection, isCut, cutEvents, cutSelection, clearSelection, hideSelectedEvents, selectedGroupKeys, setSelectedGroupKeys, setSelectedRanges, selectedRanges, legendSelectionActive } from "../../../../store/selectionStore";

import { persistantStore } from "../../../../store/persistantStore";
import { apiEndpoints } from "@config/env";
import { getData, getEventTimes, putData, formatDateTime } from "../../../../utils/global";
import { isCancelledEventTimesResponse, runEventTimesSelectionSync } from "../../../../utils/eventTimesSelection";
import ManeuverSettings from "../../../../components/menus/ManeuverSettings";
import PerformanceFilterSummary from "../../../../components/legends/PerformanceFilterSummary";
import { persistentSettingsService } from "../../../../services/persistentSettingsService";
import { getProjectManeuverFilters } from "../../../../services/projectFiltersService";
import { user } from "../../../../store/userStore";
import { MANEUVER_EVENT_TYPES, normalizeManeuverEventType } from "../../../../utils/maneuverEventType";
import { clearManeuverSelectionOnEventTypeChange } from "../../../../utils/maneuverEventTypeSelection";
import { useOpenGradeAllForMapClick } from "../../../../utils/maneuverMapClickGrade";
import { useAssistantPageScope } from "../../../../hooks/useAssistantPageScope";
import {
  selectedGradesManeuvers,
  selectedStatesManeuvers,
  selectedRacesManeuvers,
  selectedLegsManeuvers,
  selectedRacesTimeseries,
  selectedLegsTimeseries,
  isTrainingHourMode,
  setSelectedGradesManeuvers,
  setSelectedStatesManeuvers,
  setSelectedRacesManeuvers,
  setSelectedLegsManeuvers,
  maneuverTrainingRacing,
  setManeuverTrainingRacing,
  maneuverTackEntry,
  setManeuverTackEntry,
  raceOptions as storeRaceOptions,
  legOptions as storeLegOptions,
  gradeOptions as storeGradeOptions,
  setRaceOptions as pushRaceOptionsToFilterStore,
  setLegOptions as pushLegOptionsToFilterStore,
  setGradeOptions as pushGradeOptionsToFilterStore,
} from "../../../../store/filterStore";
import { toggleGroupedLegendSelection, collectManeuverIdsForLegendItem } from "../../../../utils/colorGrouping";
import { mediaAvailabilityService } from "../../../../services/mediaAvailabilityService";
import {
  MANEUVER_VIDEO_START_OFFSET_SECONDS,
  timeWindow,
  getDisplayWindowReferenceTime,
  exploreMapMountedCount,
  isPlaying,
  clearManeuverPlayhead,
} from "../../../../store/playbackStore";
import {
  applyMapTimeScopeIfActive,
  computeExploreMapReportActiveTimeWindow,
  fleetActiveTimeWindowScopeKey,
  fleetMapRaceLegFilterSignature,
  mapReportScopeSignature,
  quantizeFleetReferenceTime,
  readFleetMapRaceLegFilters,
  parseFleetMapRaceSelections,
  parseFleetMapLegSelections,
  type MapScopeContext,
  isExploreMapActiveForReports,
} from "../../../../utils/fleetTimeWindow";
import { getCurrentDatasetTimezone } from "../../../../store/datasetTimezoneStore";
import { shouldBlockGradeLikeHotkeysForPerformanceSelections } from "../../../../utils/performanceLocalSelection";
import { TAKEOFF_CHANNELS, ACCELERATION_ACCMAX_CHANNELS, ACCELERATION_BUILD_VMG_CHANNELS, RIDE_HEIGHT_CHANNELS, buildManeuversChannelsRequest } from "../../../../utils/maneuversConfig";
import {
  mapManeuverRowsToScatterTimeseriesPoints,
  maneuverTimelineLegendGroups,
} from "../../../../utils/maneuverScatterTimeseries";
import ManeuverColorLegend from "../../../../components/legends/ManeuverColorLegend";
import {
  buildManeuverColorLegendItems,
  applyManeuverLegendSelection,
} from "../../../../utils/maneuverColorLegend";
import { maneuverColorLegendReservePx } from "../../../../utils/maneuverLegendHeight";
import { maneuverBaselineLabel } from "../../../../utils/maneuverBaselineLegend";
import { maneuverBaselineStore } from "../../../../store/maneuverBaselineStore";
import BaselineDisplayModal from "../../../../components/modals/BaselineDisplayModal";
import { useManeuverBaselineDisplay } from "../../../../hooks/useManeuverBaselineDisplay";
import { useManeuverBaselineLoader } from "../../../../utils/maneuverBaseline";
import { applyManeuverEntrySide } from "../../../../utils/maneuverTackEntryFilter";
import { passesManeuverRaceFilter, passesManeuverTrainingRacingFilter } from "../../../../utils/maneuverRaceFilter";
import { applyManeuverFoilingStateAfterFetch, reconcileManeuverStatesWithBaseline } from "../../../../utils/maneuverFoilingState";
import { fetchDatasetManeuverStateOptions } from "../../../../utils/maneuverStateOptionsService";
import {
  selectionRangesSignatureForEvents,
  buildSelectedRangesForEvents,
  applyCutToRows,
} from "../../../../utils/selectionRangeHelpers";
import { createManeuverRangeColorResolver } from "../../../../utils/selectionColors";
import { sourcesStore } from "../../../../store/sourcesStore";

const { selectedClassName, selectedProjectId, selectedDatasetId, selectedSourceId, selectedSourceName } = persistantStore;

// Channels list for maneuvers endpoints - matches server_app/controllers/data.js lines 457-484
const MANEUVERS_CHANNELS = [
  'Tws_bin',
  'Tws_avg',
  'Twa_entry',
  'Twa_entry_n',
  'Vmg_perc_avg',
  'Loss_inv_tgt',
  'Loss_turn_tgt',
  'Loss_build_tgt',
  'Loss_total_tgt',
  'Mmg',
  'Bsp_drop',
  'Bsp_min',
  'Bsp_min_delta',
  'Drop_time',
  'Pop_time',
  'Time_two_boards',
  'Raise_time',
  'Accel_slope',
  'Decel_slope',
  'Lwy_max',
  'Turn_radius',
  'Accel_max',
  'Accel_min',
  'Twa_exit_n',
  'Twa_build_n',
  'Twa_drop_n',
  'Turn_rate_max',
  'Turn_angle_max',
  'Overshoot_angle',
  'Overshoot_perc',
  'Time_raising',
  'Time_dropping',
  'Rud_ang_max',
  'Twd_delta',
  'Rake_drop',
  'Aoa_drop',
  'Heel_drop',
  'Rake_aoa_min_old_turn',
  'Aoa_raise',
  'Pitch_raise',
  'Heel_lock',
  'Rake_min_old_turn',
  'Rake_max_new_turn',
  'Rake_removed_old_time_turn',
  'Rake_added_new_time_turn',
  'Cant_drop_tgt',
  ...ACCELERATION_BUILD_VMG_CHANNELS,
  ...ACCELERATION_ACCMAX_CHANNELS,
  ...RIDE_HEIGHT_CHANNELS,
];

interface DatasetReportSplitViewProps {
  isInSplitView?: boolean;
  mainPanelMenu?: string | null;
  rightPanelMenu?: string | null;
}

export default function ManeuversPage(props: DatasetReportSplitViewProps = {}) {
  // Use dataset context for this component
  const context = 'dataset';

  const baselineDisplay = useManeuverBaselineDisplay({
    getClassName: () => selectedClassName(),
    getProjectId: () => selectedProjectId(),
    getEventType: () => eventType(),
    onApplied: () => setTriggerUpdate(true),
  });
  // Publishes the chosen fit: TWS ALL draws the scatter curve; a numeric TWS bin draws map,
  // timeseries and the pinned table row.
  useManeuverBaselineLoader({ tws, eventType, context });

  const [eventTypes] = createSignal<string[]>(['TACK','GYBE','ROUNDUP','BEARAWAY','TAKEOFF']);
  const [phases] = createSignal<string[]>(['FULL','INVESTMENT','TURN','ACCELERATION']);
  const [colors] = createSignal<string[]>(['TWS','VMG','TACK','CONFIG','CREW','RACE','STATE','TESTS']);
  const [twsoptions, setTwsOptions] = createSignal<string[]>([]);

  const [views] = createSignal<string[]>(['MAP','TABLE','SCATTER','TIME SERIES']);
  const [view, setView] = createSignal<'MAP' | 'TABLE' | 'SCATTER' | 'TIME SERIES' | 'VIDEO'>('MAP');
  useActiveManeuverViewMirror(view);
  // VIDEO mode: toggle the left column between the data table and a playhead-synced timeseries.
  // Ephemeral — reset back to TABLE whenever we leave VIDEO.
  const [videoSidePanel, setVideoSidePanel] = createSignal<'TABLE' | 'CHART'>('TABLE');
  createEffect(on(view, (v) => { if (v !== 'VIDEO') { setVideoSidePanel('TABLE'); } }));
  // Clearing the selection returns the video panel to the data table (the chart needs a selection).
  createEffect(() => { if (videoSidePanel() === 'CHART' && selectedEvents().length === 0) { setVideoSidePanel('TABLE'); } });
  // MAP mode: same toggle, but the playhead drag moves a boat on the map instead of seeking video.
  const [mapSidePanel, setMapSidePanel] = createSignal<'TABLE' | 'CHART'>('TABLE');
  createEffect(on(view, (v) => { if (v !== 'MAP') { setMapSidePanel('TABLE'); clearManeuverPlayhead(); } }));
  createEffect(() => { if (mapSidePanel() === 'CHART' && selectedEvents().length === 0) { setMapSidePanel('TABLE'); } });
  const [groupedOptions] = createSignal<string[]>(['OFF','ON','MIX']);

  // Timeseries description options
  // Filter state for ManeuverSettings
  const [raceOptions, setRaceOptions] = createSignal<(number | string)[]>([]);
  const [legOptions, setLegOptions] = createSignal<number[]>([]);
  const [gradeOptions, setGradeOptions] = createSignal<number[]>([]);
  const [stateOptions, setStateOptions] = createSignal<string[]>([]);
  const [selectedRaces, setSelectedRaces] = createSignal<(number | string)[]>([]);
  const [selectedLegs, setSelectedLegs] = createSignal<number[]>([]);
  const [selectedGrades, setSelectedGrades] = createSignal<number[]>([]);
  const [selectedStates, setSelectedStates] = createSignal<string[]>([]);
  const [mapClickGradeReady, setMapClickGradeReady] = createSignal(false);

  useAssistantPageScope(() => ({
    scope_kind: 'dataset',
    page_details: {
      event_type: eventType(),
      view: view(),
      races: selectedRaces(),
      legs: selectedLegs(),
      grades: selectedGrades(),
      states: selectedStates(),
      phase: phase(),
    },
  }));

  /**
   * True when the maneuver type on screen has a REMEMBERED state selection, which is what stops
   * option discovery replacing it with "every state this type has". Owned by
   * useManeuverStatesForEventType below — the answer is per event type, because the vocabularies are
   * disjoint (H-ladder vs round up / bear away geometry).
   */
  let hasSavedStateFilter = false;

  const { persistStates, applyProjectDefaultStates } = useManeuverStatesForEventType({
    eventType,
    selectedStates,
    applySelection: (next) => {
      setSelectedStates(next);
      setSelectedStatesManeuvers(next);
    },
    setHasSavedStates: (value) => { hasSavedStateFilter = value; },
    userId: () => user()?.user_id ?? '',
    className: () => selectedClassName() ?? '',
    projectId: () => selectedProjectId() ?? 0,
    suppressInitialRestore: () => willRestoreShare('Maneuvers'),
  });

  // Timeline visibility state
  const [showTimeline, setShowTimeline] = createSignal<boolean>(true);

  // Handler for timeline changes
  const handleTimelineChange = (value: boolean) => {
    setShowTimeline(value);
  };

  const MANEUVERS_PAGE_KEY = 'Maneuvers';
  useDashboardShareRestore(
    MANEUVERS_PAGE_KEY,
    () =>
      buildManeuversShareState({
        ...currentManeuverBaselineShare(),
        view: view(),
        showTimeline: showTimeline(),
        eventType: eventType(),
        phase: phase(),
        color: color(),
        tws: tws(),
        grouped: grouped(),
        groupDisplayMode: groupDisplayMode(),
        selectedRaces: selectedRaces(),
        selectedLegs: selectedLegs(),
        selectedGrades: selectedGrades(),
        selectedStates: selectedStates(),
        maneuverTrainingRacing: maneuverTrainingRacing(),
        maneuverTackEntry: maneuverTackEntry(),
        selectedEvents: selectedEvents(),
        hasSelection: hasSelection(),
        maneuverSelection: selection(),
      }),
    (obj) =>
      applyManeuversShareState(obj, MANEUVERS_PAGE_KEY, {
        setView,
        setShowTimeline,
        setSelectedRaces,
        setSelectedLegs,
        setSelectedGrades,
        setSelectedStates,
        applySharedBaseline: (value: unknown) => { void baselineDisplay.showSharedBaseline(value); },
      })
  );

  // Memoize view options to prevent SolidJS computation warnings. VIDEO only when group is OFF and video menu is available.
  const viewOptions = createMemo<string[]>(() => {
    const base = grouped() ? ['MAP','TABLE','BOXES','TIME SERIES'] : views();
    if (grouped() || !hasVideoMenu()) {
      return base;
    }
    return [...base, 'VIDEO'];
  });

  // Abort controllers for canceling requests
  let tableDataController: AbortController | null = null;
  let stateOptionsController: AbortController | null = null;

  const applyDiscoveredStateOptions = (discovered: string[]) => {
    if (discovered.length === 0) return;
    setStateOptions(discovered);
    const reconciled = reconcileManeuverStatesWithBaseline(
      selectedStates(),
      discovered,
      hasSavedStateFilter
    );
    if (
      reconciled.length !== selectedStates().length ||
      reconciled.some((s, i) => s !== selectedStates()[i])
    ) {
      setSelectedStates(reconciled);
      setSelectedStatesManeuvers(reconciled);
    }
  };

  const refreshManeuverStateOptions = async () => {
    const requestEventType = (eventType() || 'TACK').trim() || 'TACK';
    const className = selectedClassName();
    const projectId = selectedProjectId();
    const datasetId = selectedDatasetId();
    if (!className || !projectId || !datasetId) return;

    if (stateOptionsController) stateOptionsController.abort();
    stateOptionsController = new AbortController();

    const discovered = await fetchDatasetManeuverStateOptions({
      className,
      projectId,
      datasetId,
      eventType: requestEventType,
      signal: stateOptionsController.signal,
    });
    if ((eventType() || 'TACK').trim() !== requestEventType) return;
    applyDiscoveredStateOptions(discovered);
  };
  let timesController: AbortController | null = null;

  const fetchTableData = async () => {
    // Cancel previous request if still pending
    if (tableDataController) {
      tableDataController.abort();
    }

    // Create new abort controller
    tableDataController = new AbortController();

    // Capture event type for this request so we don't apply a stale response (e.g. GYBE) over current type (e.g. TACK)
    const requestEventType = (eventType() || 'TACK').trim() || 'TACK';

    try {
      // Build filters so grade, state, and training/racing are applied server-side
      const filters: Record<string, number[] | string[]> = {};
      const grades = selectedGrades();
      if (grades.length > 0) {
        filters.GRADE = grades.map(g => Number(g)).filter(n => !isNaN(n));
      }
      const states = selectedStates();
      if (states.length > 0) {
        filters.STATE = states;
      }
      const trainingRacingVal = maneuverTrainingRacing();
      if (trainingRacingVal === 'TRAINING' || trainingRacingVal === 'RACING') {
        filters.TRAINING_RACING = [trainingRacingVal];
      }
      // TAKEOFF needs twa_entry (from Twa_start) so we can set tack to Port/Stbd
      const channelsToRequest = buildManeuversChannelsRequest(requestEventType, MANEUVERS_CHANNELS, {
        takeoffPrefix: ['twa_entry'],
      });
      let url = `${apiEndpoints.app.data}/maneuvers-table-data?class_name=${encodeURIComponent(selectedClassName())}&project_id=${encodeURIComponent(selectedProjectId())}&dataset_id=${encodeURIComponent(selectedDatasetId())}&event_type=${encodeURIComponent(requestEventType)}&channels=${encodeURIComponent(JSON.stringify(channelsToRequest))}`;
      if (Object.keys(filters).length > 0) {
        url += `&filters=${encodeURIComponent(JSON.stringify(filters))}`;
      }
      let result_json = await getData(url, tableDataController.signal);

      // Check if request was cancelled (getData returns this instead of throwing)
      if (result_json?.type === 'AbortError' || result_json?.error === 'Request cancelled') {
        // Request was cancelled - this is expected, no error needed
        return;
      }

      // Ignore response if user changed maneuver type before this request completed (avoid stale overwrite)
      if ((eventType() || 'TACK').trim() !== requestEventType) {
        return;
      }

      if (result_json.success && result_json.data && Array.isArray(result_json.data)) {
        let data = result_json.data;

        // Add datetimeLocal (dataset local time) for table and tooltips
        data.forEach((item: any) => {
          const tz = (item.timezone ?? item.Timezone ?? "").trim() || undefined;
          const rawDt = item.Datetime ?? item.datetime;
          if (rawDt && tz) {
            try {
              const formatted = formatDateTime(rawDt, tz);
              if (formatted) item.datetimeLocal = formatted;
            } catch {
              // leave datetimeLocal undefined
            }
          }
        });

        // TAKEOFF, BEARAWAY, ROUNDUP: Port/Stbd from entry or build TWA (like bearaway/roundup), not S-P / P-S
        const evt = (requestEventType || '').toUpperCase();
        const usePortStbd = evt === 'TAKEOFF' || evt === 'BEARAWAY' || evt === 'ROUNDUP';
        if (usePortStbd) {
          data.forEach((item: any) => {
            const twa = item.twa_entry ?? item.twa_build ?? item.Twa_start ?? 0;
            item.tack = Number(twa) > 0 ? 'STBD' : 'PORT';
          });
        } else if (phase() == 'FULL' || phase() == 'TURN') {
          data.forEach((item) => {
            item.tack = item.twa_entry > 0 ? 'S - P' : 'P - S';
          });
        } else if (phase() == 'INVESTMENT') {
          data.forEach((item) => {
            item.tack = item.twa_entry > 0 ? 'STBD' : 'PORT';
          });
        } else {
          data.forEach((item) => {
            item.tack = item.twa_entry > 0 ? 'PORT' : 'STBD';
          });
        }

        // Generate TWS bins in increments of 5 for GP50
        // Find min and max TWS values from the data
        const twsValues: number[] = [];
        data.forEach((item: any) => {
          const twsValue = item.tws_avg ?? item.tws_bin;
          if (twsValue !== null && twsValue !== undefined && !isNaN(Number(twsValue))) {
            twsValues.push(Number(twsValue));
          }
        });

        const tws_bins: string[] = ['ALL'];
        if (twsValues.length > 0) {
          const minTws = Math.min(...twsValues);
          const maxTws = Math.max(...twsValues);
          
          // Round min down to nearest multiple of 5, round max up to nearest multiple of 5
          const minBin = Math.floor(minTws / 5) * 5;
          const maxBin = Math.ceil(maxTws / 5) * 5;
          
          // Generate bins in increments of 5
          for (let bin = minBin; bin <= maxBin; bin += 5) {
            tws_bins.push(String(bin));
          }
        }
        setTwsOptions(tws_bins);

        const baseline = stateOptions();
        const { reconciled } = applyManeuverFoilingStateAfterFetch(
          data,
          selectedStates(),
          hasSavedStateFilter,
          { availableStates: baseline }
        );
        if (
          reconciled.length !== selectedStates().length ||
          reconciled.some((s, i) => s !== selectedStates()[i])
        ) {
          setSelectedStates(reconciled);
          setSelectedStatesManeuvers(reconciled);
        }

        setManeuvers(data)

        // Pass data directly to filterData to avoid race condition with store updates.
        // Do not setFiltered here — filterData applies TWS/grade/state filters and sets filtered once (avoids map fetching full list then filtered list).
        filterData(data)
      } else {
        // Handle API error response - reset to empty state only if this response is still current
        if ((eventType() || 'TACK').trim() !== requestEventType) return;
        const errorMessage = result_json?.message || result_json?.error || (result_json?.success === false ? 'API returned unsuccessful response' : 'Unknown error');
        logWarning('Failed to fetch table data:', errorMessage, result_json);
        setManeuvers([]);
        setTableData([]);
        setFiltered([]);
        setTwsOptions(['ALL']);
      }
    } catch (error: unknown) {
      if (error instanceof Error && error.name === 'AbortError') {
        // Request was cancelled - this is expected, no error needed
      } else {
        if ((eventType() || 'TACK').trim() === requestEventType) {
          logError('Error fetching table data:', error as any);
          setManeuvers([]);
          setTableData([]);
          setFiltered([]);
          setTwsOptions(['ALL']);
        }
      }
    } finally {
      tableDataController = null;
    }
  }

  const handleEventType = (val: string) => {
    const prev = normalizeManeuverEventType(eventType());
    const next = normalizeManeuverEventType(val);
    if (next === prev) return;
    setEventType(next);
    clearManeuverSelectionOnEventTypeChange('Maneuvers');
    // States are swapped by useManeuverStatesForEventType, which restores the incoming type's own
    // chips. The TAKEOFF special case that used to live here re-ran the whole settings load on the
    // way out, which also re-applied the saved grade / race / leg filters over whatever the user
    // had set — the state swap was never the only thing it did.
    void fetchTableData();
  };

  const handlePhase = (val: string) => {
    setPhase(val)
    setTriggerUpdate(true);
  };

  const handleView = async (val: string) => {
    const normalized = (val === 'BOXES') ? 'SCATTER' : val;
    // Guard to satisfy the view signal's union type
    if (normalized === 'MAP' || normalized === 'TABLE' || normalized === 'SCATTER' || normalized === 'TIME SERIES' || normalized === 'VIDEO') {
      setView(normalized);
      const { project_id, dataset_id } = await getCurrentProjectDatasetIds();
      await logActivity(project_id, dataset_id, 'Maneuvers.tsx', 'Maneuvers Report', `View changed to ${normalized}`);
      if (normalized === 'VIDEO') {
        const data = maneuvers();
        const first = data?.[0];
        const raw = first?.datetime ?? first?.Datetime ?? first?.DATETIME ?? first?.date ?? first?.Date;
        if (raw != null && (typeof raw !== 'string' || raw.trim() !== '')) {
          try {
            const d = typeof raw === 'string' ? new Date(raw.trim()) : new Date(raw);
            if (!Number.isNaN(d.getTime())) {
              const dateYmd = mediaAvailabilityService.getDateYmdFromDatetime(d, getCurrentDatasetTimezone());
              await mediaAvailabilityService.preloadForDate(dateYmd, getCurrentDatasetTimezone());
            }
          } catch (_) { /* empty */ }
        }
      }
      // Always re-run filterData on view change: VIDEO view filters by has-video; other views show all (grade/TWS/etc) maneuvers
      filterData();
    }
    setTriggerUpdate(true);
  };

  const handleColor = async (val: string) => {
    setColor(val)
    const { project_id, dataset_id } = await getCurrentProjectDatasetIds();
    await logActivity(project_id, dataset_id, 'Maneuvers.tsx', 'Maneuvers Report', `Color changed to ${val}`);
    setTriggerUpdate(true)
  };

  const exploreMapTimeWindowContext = (): MapScopeContext => ({
    exploreMapMountedCount: exploreMapMountedCount(),
    isInSplitView: props.isInSplitView,
    mainPanelMenu: props.mainPanelMenu,
    rightPanelMenu: props.rightPanelMenu,
  });

  const exploreMapActive = createMemo(() =>
    isExploreMapActiveForReports(exploreMapTimeWindowContext())
  );

  const exploreActiveTimeWindow = createMemo(() => {
    const ref = getDisplayWindowReferenceTime();
    const quantizedRef = isPlaying()
      ? quantizeFleetReferenceTime(ref, 1000)
      : ref;
    return computeExploreMapReportActiveTimeWindow(
      exploreMapTimeWindowContext(),
      timeWindow(),
      quantizedRef
    );
  });

  const exploreWindowScopeKey = createMemo(() =>
    fleetActiveTimeWindowScopeKey(exploreActiveTimeWindow(), 1000)
  );

  const exploreReportScopeSig = createMemo(() =>
    mapReportScopeSignature(exploreMapActive(), exploreWindowScopeKey())
  );

  // When explore map is active, mirror map race/leg filters into maneuver report filters.
  let lastExploreMapRaceLegSig = "";
  createEffect(() => {
    if (!exploreMapActive()) return;
    void selectedRacesTimeseries();
    void selectedLegsTimeseries();
    const sig = fleetMapRaceLegFilterSignature();
    if (sig === lastExploreMapRaceLegSig) return;
    lastExploreMapRaceLegSig = sig;
    const { races, legs } = readFleetMapRaceLegFilters();
    if (isTrainingHourMode()) {
      setSelectedRaces([]);
      setSelectedRacesManeuvers([]);
    } else {
      setSelectedRaces(parseFleetMapRaceSelections(races));
      setSelectedRacesManeuvers(races);
    }
    setSelectedLegs(parseFleetMapLegSelections(legs));
    setSelectedLegsManeuvers(legs);
    if (untrack(() => maneuvers().length > 0)) {
      filterData();
    }
  });

  const sortedEventIdsEqual = (a: unknown[], b: number[]): boolean => {
    if (!Array.isArray(a) || a.length !== b.length) return false;
    const toId = (x: unknown) => (typeof x === 'number' ? x : (x as { event_id?: number })?.event_id);
    const numsA = a.map(toId).filter((id): id is number => typeof id === 'number' && id > 0).sort((x, y) => x - y);
    const numsB = [...b].filter((id): id is number => typeof id === 'number' && id > 0).sort((x, y) => x - y);
    if (numsA.length !== numsB.length) return false;
    return numsA.every((v, i) => v === numsB[i]);
  };

  const filterData = (dataOverride?: any[]) => {
    // Use provided data or read from store - this avoids race conditions
    const maneuversData = dataOverride ?? maneuvers();
    if (!maneuversData || !Array.isArray(maneuversData) || maneuversData.length === 0) {
      const hadFiltered = filtered().length > 0;
      setFiltered([]);
      setTableData([]);
      if (hadFiltered) {
        setTriggerUpdate(true);
      }
      return;
    }

    let filteredData_tws: any[] = []

    if (tws() == 'ALL') {
      filteredData_tws = maneuversData;
    } else {
      // For gp50, filter by TWS bin (intervals of 5, ±2.5 from selected value)
      const selectedTws = Number(tws());
      if (!isNaN(selectedTws)) {
        filteredData_tws = maneuversData.filter((item: any) => {
          const itemTws = item.tws_bin ?? item.tws_avg;
          if (itemTws === null || itemTws === undefined || isNaN(Number(itemTws))) {
            return false;
          }
          const itemTwsNum = Number(itemTws);
          // Filter within ±2.5 of the selected bin value
          // Bin 5 includes values from 2.5 to 7.5 (exclusive on upper bound to avoid overlap)
          const minTws = selectedTws - 2.5;
          const maxTws = selectedTws + 2.5;
          return itemTwsNum >= minTws && itemTwsNum < maxTws;
        });
      } else {
        filteredData_tws = [];
      }
    }

    // Apply grade filter using selectedGrades array - use "greater than" logic
    // Use lowercase field names as primary with fallbacks for backward compatibility
    let filteredData: any[] = filteredData_tws;
    const grades = selectedGrades();
    if (grades.length > 0) {
      // For radio button behavior, only the first selected grade is used
      // Filter for grades greater than the selected grade
      const minGrade = Math.min(...grades.map(g => Number(g)).filter(g => !isNaN(g)));
      filteredData = filteredData_tws.filter(item => {
        const itemGrade = item.grade ?? item.Grade ?? item.GRADE;
        if (itemGrade == null || itemGrade === undefined) return false;
        const numGrade = Number(itemGrade);
        if (isNaN(numGrade)) return false;
        return numGrade > minGrade;
      });
    }

    // Apply state filter using selectedStates array
    // Use lowercase field names as primary with fallbacks for backward compatibility
    const states = selectedStates();
    if (states.length > 0) {
      filteredData = filteredData.filter(item => {
        const itemState = item.state ?? item.State ?? item.STATE;
        if (itemState == null || itemState === undefined) return false;
        const stateStr = String(itemState).trim();
        return states.some(selectedState => String(selectedState).trim().toLowerCase() === stateStr.toLowerCase());
      });
    }

    const races = selectedRaces();
    if (races.length > 0) {
      filteredData = filteredData.filter((item) => passesManeuverRaceFilter(item, races));
    }

    // Apply leg filter using selectedLegs array
    // Use lowercase field names as primary with fallbacks for backward compatibility
    const legs = selectedLegs();
    if (legs.length > 0) {
      filteredData = filteredData.filter(item => {
        const legValue = item.leg_number ?? item.Leg_number ?? item.LEG;
        if (legValue == null || legValue === undefined) return false;
        return legs.includes(Number(legValue));
      });
    }

    const trainingRacing = maneuverTrainingRacing();
    if (trainingRacing) {
      filteredData = filteredData.filter((item) => passesManeuverTrainingRacingFilter(item, trainingRacing));
    }

    filteredData = applyManeuverEntrySide(filteredData, maneuverTackEntry()) as typeof filteredData;

    // If we have cut events, only show the cut maneuvers (by event id, or by time for a brush cut)
    if (isCut() && cutEvents().length > 0) {
      filteredData = applyCutToRows(filteredData, cutEvents());
    }

    // When VIDEO view: only show rows that have video (maneuver time falls inside a media window)
    if (view() === 'VIDEO') {
      const tz = getCurrentDatasetTimezone();
      const sourceName = selectedSourceName?.() ?? '';
      const eventIdsWithVideo = new Set<number>();
      for (const row of filteredData) {
        const raw = row?.datetime ?? row?.Datetime ?? row?.DATETIME ?? row?.date ?? row?.Date;
        if (raw == null || (typeof raw === 'string' && !raw.trim())) continue;
        let maneuverTime: Date;
        try {
          maneuverTime = typeof raw === 'string' ? new Date(raw.trim()) : new Date(raw);
          if (Number.isNaN(maneuverTime.getTime())) continue;
        } catch {
          continue;
        }
        if (mediaAvailabilityService.hasVideo(sourceName, maneuverTime, { timezone: tz, clipOffsetSeconds: MANEUVER_VIDEO_START_OFFSET_SECONDS })) {
          eventIdsWithVideo.add(row.event_id);
        }
      }
      filteredData = filteredData.filter((row) => eventIdsWithVideo.has(row.event_id));
    }

    filteredData = applyMapTimeScopeIfActive(
      filteredData,
      exploreMapActive(),
      exploreActiveTimeWindow()
    );

    const sortedRows = sortManeuverRows(filteredData, maneuverTableSort(), eventType());

    const eventIds = sortedRows.map(item => item.event_id);
    const mapScopePrefix = exploreMapActive()
      ? `${exploreWindowScopeKey()}:`
      : "";
    const rowsSignature = `${mapScopePrefix}${eventIds.join(",")}`;
    const prevRows = tabledata();
    const prevSignature = Array.isArray(prevRows)
      ? prevRows.map((item: { event_id?: number }) => item.event_id).join(",")
      : "";
    const prevFiltered = filtered();
    const sameEventSet =
      rowsSignature === prevSignature ||
      sortedEventIdsEqual(prevFiltered, eventIds);

    setFiltered(eventIds);
    setTableData(sortedRows);
    
    // Update selection to match filtered data (but don't override user's active selection)
    if (!hasSelection()) {
      setSelection(eventIds as any);
    }

    if (!sameEventSet) {
      setTriggerUpdate(true);
    }
  }

  useOpenGradeAllForMapClick({
    ready: mapClickGradeReady,
    grades: selectedGrades,
    setSelectedGrades,
    reload: () => {
      void fetchTableData();
    },
  });

  // Re-scope maneuvers when explore map opens/closes in split or playback window changes.
  createEffect(
    on(exploreReportScopeSig, () => {
      const data = untrack(() => maneuvers());
      if (Array.isArray(data) && data.length > 0) {
        untrack(() => filterData());
      }
    })
  );

  createEffect(() => {
    if (grouped() && view() === 'VIDEO') {
      setView('MAP');
      filterData();
    }
  });

  const handleTws = async (val: string) => {
    setTws(val)
    const { project_id, dataset_id } = await getCurrentProjectDatasetIds();
    await logActivity(project_id, dataset_id, 'Maneuvers.tsx', 'Maneuvers Report', `TWS changed to ${val}`);
    filterData()
  };

  // Filter toggle handlers
  const toggleRaceFilter = (race: number | string) => {
    const current = selectedRaces();
    const next = current.includes(race) 
      ? current.filter(r => r !== race)
      : [...current, race];
    setSelectedRaces(next);
    // Sync to global filterStore for cross-window sync
    setSelectedRacesManeuvers(next.map(r => String(r)));
  };

  const toggleLegFilter = (leg: number) => {
    const current = selectedLegs();
    const next = current.includes(leg)
      ? current.filter(l => l !== leg)
      : [...current, leg];
    setSelectedLegs(next);
    setSelectedLegsManeuvers(next.map(l => String(l)));
  };

  const toggleGradeFilter = (grade: number) => {
    const current = selectedGrades();
    const next = current.includes(grade)
      ? []
      : [grade];
    setSelectedGrades(next);
    setSelectedGradesManeuvers(next.map(g => String(g)));
  };

  const toggleStateFilter = (state: string) => {
    const current = selectedStates();
    const next = current.includes(state)
      ? current.filter(s => s !== state)
      : [...current, state];
    setSelectedStates(next);
    setSelectedStatesManeuvers(next);
    // Remembered against the maneuver type on screen, so a type switch brings back its own chips.
    persistStates();
  };

  // Keep filterStore race/leg/grade options in sync with local signals so Sidebar can broadcast them to maneuver popups
  // (e.g. Filters.tsx updates local options without going through ManeuverSettings’ filterStore writes).
  createEffect(() => {
    const rLocal = raceOptions().map((x) => String(x));
    const lLocal = legOptions().map((x) => String(x));
    const gLocal = gradeOptions().map((x) => String(x));
    const rs = storeRaceOptions().map((x) => String(x));
    const ls = storeLegOptions().map((x) => String(x));
    const gs = storeGradeOptions().map((x) => String(x));
    if (JSON.stringify([...rLocal].sort()) !== JSON.stringify([...rs].sort())) {
      pushRaceOptionsToFilterStore(rLocal);
    }
    if (JSON.stringify([...lLocal].sort()) !== JSON.stringify([...ls].sort())) {
      pushLegOptionsToFilterStore(lLocal);
    }
    if (JSON.stringify([...gLocal].sort()) !== JSON.stringify([...gs].sort())) {
      pushGradeOptionsToFilterStore(gLocal);
    }
  });

  const handleLegendClick = (legendItem: string) => {
    if (grouped()) {
      setSelectedGroupKeys((prev) => toggleGroupedLegendSelection(prev, legendItem, color() || 'TWS'));
      setTriggerSelection(true);
      return;
    }

    const dataSource = maneuvers();
    if (!dataSource || !Array.isArray(dataSource) || dataSource.length === 0) {
      logWarning('Maneuvers: Legend click on', legendItem, '- no maneuvers data available');
      return;
    }

    const currentColorField = color() || 'TWS';
    const filteredData = dataSource.filter((m: any) => filtered().includes(m.event_id));
    const intervalCalculationData = currentColorField === 'VMG' ? filteredData : dataSource;

    const legendEntryIds = collectManeuverIdsForLegendItem(dataSource, currentColorField, legendItem, {
      vmgIntervalSource: intervalCalculationData,
    });

    if (legendEntryIds.length === 0) {
      logWarning('Maneuvers: Legend click on', legendItem, '- no matching event IDs found');
      return;
    }

    // Get current selection
    const currentSelected = selectedEvents();
    const currentSelectedSet = new Set(currentSelected);
    
    // Check if all legend entry IDs are already selected
    const allSelected = legendEntryIds.every(id => currentSelectedSet.has(id));
    
    if (allSelected) {
      // All IDs are selected → remove all legend entry IDs
      const updatedSelection = currentSelected.filter(id => !legendEntryIds.includes(id));
      if (updatedSelection.length > 0) {
        setLegendSelectionActive(true);
      }
      setSelectedEvents(updatedSelection);
      logDebug('Maneuvers: Legend click on', legendItem, '- deselected all', legendEntryIds.length, 'event IDs');
    } else {
      // Not all IDs are selected → select all legend entry IDs (merge with existing)
      const updatedSelection = Array.from(new Set([...currentSelected, ...legendEntryIds]));
      setLegendSelectionActive(true);
      setSelectedEvents(updatedSelection);
      logDebug('Maneuvers: Legend click on', legendItem, '- selected all', legendEntryIds.length, 'event IDs');
    }
    
    // Refresh selection styling without refetching timeseries data
    setTriggerSelection(true);
  };

  // Event types to target for a tag write - a single selected type, else all maneuver types.
  const maneuverEventTypes = (): string[] => {
    const currentEventType = eventType();
    return currentEventType ? [currentEventType] : MANEUVER_EVENT_TYPES;
  };

  // Shared post-write handling for tag updates.
  const afterTagUpdate = async (): Promise<void> => {
    setSelectedEvents([]);
    setHasSelection(false);
    setSelection([]);
    setTriggerSelection(true);
    await fetchTableData();
  };

  // Grade update from main maneuver page (hotkeys 0-5)
  const performGradeUpdate = async (gradeValue: number, selected: number[]): Promise<void> => {
    const className = selectedClassName();
    const projectId = selectedProjectId();
    if (!className || !projectId) {
      logError('Cannot update GRADE: missing class_name or project_id');
      return;
    }
    const eventTypes = maneuverEventTypes();
    try {
      const response = await putData(`${apiEndpoints.admin.events}/tags`, {
        class_name: className,
        project_id: projectId,
        events: selected,
        event_types: eventTypes,
        key: 'GRADE',
        value: gradeValue
      });
      if (response.success) {
        logDebug('Maneuvers: Successfully updated GRADE to', gradeValue, 'for', selected.length, 'event(s)');
        const currentGrades = selectedGradesManeuvers();
        const gradeValueStr = String(gradeValue);
        if (!currentGrades.includes(gradeValueStr)) {
          setSelectedGradesManeuvers([...currentGrades, gradeValueStr]);
        }
        await afterTagUpdate();
      } else {
        logError('Failed to update GRADE:', response.message || 'Unknown error');
      }
    } catch (error: unknown) {
      logError('Error updating GRADE:', error);
    }
  };

  createEffect(
    on(
      () => normalizeManeuverEventType(eventType()),
      async (_et, prevEt) => {
        if (prevEt === undefined) return;
        await refreshManeuverStateOptions();
        void untrack(() => fetchTableData());
      },
      { defer: true }
    )
  );

  // React to cut events - refilter when cut state changes
  createEffect(on([isCut], () => {
    if (untrack(() => maneuvers().length > 0)) {
      filterData();
    }
  }));

  // React to filter changes - refilter when filters change (client-side)
  createEffect(on([selectedGrades, selectedStates, selectedRaces, selectedLegs, maneuverTackEntry], () => {
    if (untrack(() => maneuvers().length > 0)) {
      filterData();
    }
  }));

  // React to training/racing filter - refetch so API applies TRAINING_RACING in SQL, or shows all when null (All)
  createEffect(
    on(
      () => (maneuverTrainingRacing()),
      (tr) => {
        if (untrack(() => selectedDatasetId() && eventType())) {
          untrack(() => fetchTableData());
          untrack(() => filterData());
        }
      },
      { defer: true }
    )
  );

  // Sync filterStore to local state (bidirectional sync for cross-window updates)
  // This ensures that when filters change in ManeuverWindow or other windows, they sync to local state
  // Use untrack to read local state without tracking it - we only want to react to filterStore changes
  // Track a flag to prevent syncing during local updates
  let isUpdatingFromLocal = false;
  createEffect(() => {
    const filterStoreGrades = selectedGradesManeuvers();
    const filterStoreStates = selectedStatesManeuvers();
    const filterStoreRaces = selectedRacesManeuvers();
    const filterStoreLegs = selectedLegsManeuvers();
    
    // Skip if we're in the middle of a local update (to prevent loops)
    if (isUpdatingFromLocal) {
      return;
    }
    
    // Only update local state if filterStore values differ from local state
    // This prevents infinite loops and only syncs when filterStore changes from cross-window updates
    // Use untrack to read local state without creating a dependency on it
    const localGrades = untrack(() => selectedGrades());
    const localStates = untrack(() => selectedStates());
    const localRaces = untrack(() => selectedRaces());
    const localLegs = untrack(() => selectedLegs());
    
    // Convert filterStore values (strings) to local state format (numbers for grades/legs, strings for states/races)
    const filterStoreGradesNums = filterStoreGrades.map(g => Number(g)).filter(g => !isNaN(g));
    const filterStoreRacesMixed = filterStoreRaces.map(r => {
      if (r === 'TRAINING' || r === 'training') return 'TRAINING';
      const num = Number(r);
      return isNaN(num) ? r : num;
    });
    const filterStoreLegsNums = filterStoreLegs.map(l => Number(l)).filter(l => !isNaN(l));
    
    // Check if values differ (deep comparison)
    const gradesChanged = JSON.stringify([...filterStoreGradesNums].sort()) !== JSON.stringify([...localGrades].sort());
    const statesChanged = JSON.stringify([...filterStoreStates].sort()) !== JSON.stringify([...localStates].sort());
    const racesChanged = JSON.stringify([...filterStoreRacesMixed].sort()) !== JSON.stringify([...localRaces].sort());
    const legsChanged = JSON.stringify([...filterStoreLegsNums].sort()) !== JSON.stringify([...localLegs].sort());
    
    // Update local state if filterStore changed (from cross-window sync)
    // CRITICAL: Only sync if filterStore has values - NEVER clear local filters if filterStore is empty
    // This prevents clearing filters when other pages (like Probability/Scatter) haven't initialized yet
    // or when ManeuverWindow loads and temporarily has empty filterStore
    if (gradesChanged || statesChanged || racesChanged || legsChanged) {
      isUpdatingFromLocal = true;
      try {
        // Only update if filterStore has values - never sync empty filterStore to local state
        // This ensures that Probability/Scatter can maintain their own filter state even if
        // filterStore is temporarily empty (e.g., during ManeuverWindow initialization)
        if (gradesChanged && filterStoreGradesNums.length > 0) {
          setSelectedGrades(filterStoreGradesNums);
          logDebug('Maneuvers: Synced grades from filterStore to local state', filterStoreGradesNums);
        }
        if (statesChanged && filterStoreStates.length > 0) {
          setSelectedStates(filterStoreStates);
          logDebug('Maneuvers: Synced states from filterStore to local state', filterStoreStates);
        }
        if (racesChanged && filterStoreRacesMixed.length > 0) {
          setSelectedRaces(filterStoreRacesMixed);
          logDebug('Maneuvers: Synced races from filterStore to local state', filterStoreRacesMixed);
        }
        if (legsChanged && filterStoreLegsNums.length > 0) {
          setSelectedLegs(filterStoreLegsNums);
          logDebug('Maneuvers: Synced legs from filterStore to local state', filterStoreLegsNums);
        }
      } finally {
        // Reset flag after a microtask to allow reactive effects to complete
        Promise.resolve().then(() => {
          isUpdatingFromLocal = false;
        });
      }
    }
  });

  // React to view and data so layout stays correct when filtered list updates (aligned with FleetManeuvers).
  createEffect(() => {
    view();
    filtered().length;
    maneuvers().length;
    showTimeline();
    // Colour mode changes the legend's wrapped height, so re-measure on it too.
    color();
    maneuverColorLegendItems().length;
    // Use setTimeout to ensure DOM has updated. Delegate to updateContainerHeights rather than
    // repeating its arithmetic: this effect used to carry a second copy, so anything added to the
    // reserve there (e.g. the color legend row) was silently overwritten here a tick later.
    setTimeout(() => {
      updateContainerHeights();
    }, 0);
  });

  // When VIDEO view and we have data, ensure media availability is preloaded then re-filter
  // Only run when view changes to VIDEO or when filters change the maneuvers data (same logic as FleetManeuvers)
  createEffect(on([view, maneuvers], ([currentView, data]) => {
    if (currentView !== 'VIDEO') return;
    if (!data?.length) return;
    const first = data[0];
    const raw = first?.datetime ?? first?.Datetime ?? first?.DATETIME ?? first?.date ?? first?.Date;
    if (raw == null || (typeof raw === 'string' && !raw.trim())) return;
    (async () => {
      try {
        const d = typeof raw === 'string' ? new Date(raw.trim()) : new Date(raw);
        if (Number.isNaN(d.getTime())) return;
        const tz = untrack(() => getCurrentDatasetTimezone());
        const dateYmd = mediaAvailabilityService.getDateYmdFromDatetime(d, tz);
        await mediaAvailabilityService.preloadForDate(dateYmd, tz);
        untrack(() => filterData());
      } catch (_) { /* empty */ }
    })();
  }));

  createEffect(() => {
    const eventList = selectedEvents();
    if (eventList.length === 0) return;

    const datasetId = selectedDatasetId();
    if (!datasetId || datasetId <= 0) {
      logDebug('Maneuvers: Skipping times fetch (dataset_id required)');
      return;
    }

    if (timesController) {
      timesController.abort();
    }

    timesController = new AbortController();
    const signal = timesController.signal;
    const className = selectedClassName();
    const projectId = selectedProjectId();

    void runEventTimesSelectionSync({
      fetchTimes: () =>
        getEventTimes(
          {
            class_name: className,
            project_id: projectId,
            dataset_id: datasetId,
            event_list: eventList,
          },
          signal
        ),
      applySelection: (result) => {
        if (isCancelledEventTimesResponse(result)) return;
        const data = (result as { data?: unknown })?.data;
        setSelection(Array.isArray(data) ? data : []);
      },
    }).finally(() => {
      timesController = null;
    });
  });

  // Populate selectedRanges from maneuver event times + table rows (selectionStore skips getEventTimeRanges on this page).
  let lastManeuverRangesSig = "";
  createEffect(() => {
    const ids = selectedEvents();
    const selectionRows = selection();
    void maneuvers();
    void color();
    void legendSelectionActive();
    if (!Array.isArray(ids) || ids.length === 0) {
      lastManeuverRangesSig = "";
      // Clear explicitly rather than relying on the store's follow-up, which skips the clear while a brush is
      // active (selectionStore setSelectedEvents) — otherwise a stale overlay survives on the split-view map.
      if (untrack(() => selectedRanges()).length > 0) {
        setSelectedRanges([]);
      }
      return;
    }
    const tableArr = Array.isArray(tabledata()) ? tabledata() : [];
    const maneuverArr = Array.isArray(maneuvers()) ? maneuvers() : [];
    const lookupRows =
      maneuverArr.length > 0
        ? [
            ...maneuverArr,
            ...tableArr.filter(
              (row: { event_id?: number }) =>
                !maneuverArr.some((m: { event_id?: number }) => m?.event_id == row?.event_id)
            ),
          ]
        : tableArr;

    const sources = sourcesStore.sources() ?? [];
    const resolveColor = createManeuverRangeColorResolver({
      colorMode: color(),
      selectedIds: ids,
      legendSelectionActive: legendSelectionActive(),
      lookupRows: lookupRows as Record<string, unknown>[],
      // Map ranges: no color beats a white overlay, so the renderer can fall back to the palette index.
      unselectedFallback: undefined,
    });

    const selectionTimeRows = Array.isArray(selectionRows)
      ? selectionRows.filter(
          (row) => row != null && typeof row === "object" && !Array.isArray(row) && "start_time" in row
        )
      : [];

    if (lookupRows.length === 0 && selectionTimeRows.length === 0) return;

    // Per-id build: selection() is an async getEventTimes fetch that lags the click, so ids it hasn't
    // returned yet fall back to their table row individually. Building all-or-nothing used to emit a
    // partial list that suppressed the fallback and got frozen in by the signature dedup below.
    const ranges = buildSelectedRangesForEvents({
      eventIds: ids,
      selectionData: selectionTimeRows,
      lookupRows,
      resolveColor,
      sources,
    });
    if (ranges.length === 0) return;

    const sig = selectionRangesSignatureForEvents(ids, ranges);
    if (sig === lastManeuverRangesSig) return;
    lastManeuverRangesSig = sig;
    setSelectedRanges(ranges);
  });

  // Function to sync table height with map SVG height
  const syncTableHeightWithMap = () => {
    if (view() !== 'MAP') return;
    
    const mapElement = document.querySelector('#maneuver-map') as HTMLElement | null;
    const tableArea = document.getElementById('table-area') as HTMLElement | null;
    
    if (mapElement && tableArea) {
      // Get the actual height of the map element (including SVG)
      const mapHeight = mapElement.offsetHeight || mapElement.clientHeight;
      
      // Set table area to match map height
      if (mapHeight > 0) {
        tableArea.style.height = `${mapHeight}px`;
        tableArea.style.minHeight = `${mapHeight}px`;
      }
    }
  };

  // Sync table height with layout height for VIDEO view so table and video panels match
  const syncTableHeightWithVideo = () => {
    if (view() !== 'VIDEO') return;
    const layout = document.querySelector('.maneuver-map-layout') as HTMLElement | null;
    const tableArea = document.getElementById('table-area') as HTMLElement | null;
    if (layout && tableArea) {
      const layoutHeight = layout.offsetHeight || layout.clientHeight;
      if (layoutHeight > 0) {
        tableArea.style.height = `${layoutHeight}px`;
        tableArea.style.minHeight = `${layoutHeight}px`;
      }
    }
  };

  // Function to update container heights based on actual viewport (full screen)
  const updateContainerHeights = () => {
    // Get the actual available height from the viewport (full screen)
    // Header height is 58px (50px + 8px padding)
    const headerHeight = 58;
    const timelineReserve =
      showTimeline() && filtered().length > 0 ? 120 : 0;

    // Measured, not a constant: the legend row wraps to a different height per colour mode.
    const legendReservePx = maneuverColorLegendReservePx();

    const additionalSpacingForMap = 100 + timelineReserve + legendReservePx;
    const mapHeight = window.innerHeight - headerHeight - additionalSpacingForMap;

    const additionalSpacingForTable = 80 + timelineReserve + legendReservePx;
    const tableHeight = window.innerHeight - headerHeight - additionalSpacingForTable;
    
    // Update heights dynamically for all views
    const mapLayout = document.querySelector('.maneuver-map-layout') as HTMLElement | null;
    const tableWrapper = document.getElementById('datatable-big-wrapper') as HTMLElement | null;
    
    if (mapLayout) {
      mapLayout.style.height = `${mapHeight}px`;
      mapLayout.style.minHeight = `${mapHeight}px`;
    }
    
    if (tableWrapper) {
      tableWrapper.style.height = `${tableHeight}px`;
    }
    
    // Sync table height with map SVG height for MAP view, or video panel for VIDEO view
    setTimeout(() => {
      if (view() === 'MAP') syncTableHeightWithMap();
      if (view() === 'VIDEO') syncTableHeightWithVideo();
    }, 100); // Small delay to ensure map/video is rendered
  };
  
  // Function to observe sidebar for collapse/expand changes
  const observeSidebar = () => {
    const sidebar = document.querySelector('.sidebar');
    if (!sidebar) return null;
    
    const observer = new MutationObserver(() => {
      // When sidebar class changes (collapsed/expanded), update heights
      updateContainerHeights();
    });
    
    observer.observe(sidebar, {
      attributes: true,
      attributeFilter: ['class']
    });
    
    return observer;
  };

  // Load filters from persistent settings
  const loadFiltersFromPersistentSettings = async () => {
    const currentUser = user();
    if (currentUser?.user_id) {
      try {
        const { selectedClassName, selectedProjectId } = persistantStore;
        const className = selectedClassName();
        const projectId = selectedProjectId();

        if (className && projectId) {
          const settings = await persistentSettingsService.loadSettings(
            currentUser.user_id,
            className,
            projectId
          );

          logDebug('Maneuvers: Loaded settings from persistent settings', settings);
          let gradesLoaded = false;
          if (settings?.maneuverFilters) {
            const filters = settings.maneuverFilters;
            if (filters.grades && Array.isArray(filters.grades) && filters.grades.length > 0) {
              setSelectedGrades(filters.grades);
              setSelectedGradesManeuvers(filters.grades.map((g: number) => String(g)));
              gradesLoaded = true;
              logDebug('Maneuvers: Loaded grade filters from persistent settings', filters.grades);
            }
            // filters.states is deliberately NOT read here. States are per maneuver type now
            // (useManeuverStatesForEventType), and this flat value is only the first-visit seed that
            // hook falls back to.
            if (filters.races && Array.isArray(filters.races) && filters.races.length > 0) {
              setSelectedRaces(filters.races);
              setSelectedRacesManeuvers(filters.races.map((r: number | string) => String(r)));
              logDebug('Maneuvers: Loaded race filters from persistent settings', filters.races);
            }
            if (filters.legs && Array.isArray(filters.legs) && filters.legs.length > 0) {
              setSelectedLegs(filters.legs);
              setSelectedLegsManeuvers(filters.legs.map((l: number) => String(l)));
              logDebug('Maneuvers: Loaded leg filters from persistent settings', filters.legs);
            }
            if (filters.trainingRacing === 'TRAINING' || filters.trainingRacing === 'RACING') {
              setManeuverTrainingRacing(filters.trainingRacing);
              logDebug('Maneuvers: Loaded trainingRacing filter from persistent settings', filters.trainingRacing);
            }
            if (filters.tackEntry === 'PORT' || filters.tackEntry === 'STBD') {
              setManeuverTackEntry(filters.tackEntry);
              logDebug('Maneuvers: Loaded tack entry filter from persistent settings', filters.tackEntry);
            }
          } else {
            logDebug('Maneuvers: No maneuverFilters found in settings', settings);
          }
          
          // Set default grade >1 (shows grades 2 and 3) if no grades were loaded from persistent settings
          if (!gradesLoaded) {
            setSelectedGrades([1]);
            setSelectedGradesManeuvers(['1']);
            logDebug('Maneuvers: Set default grade filter to [1] (>1)');
          }
        }
      } catch (error) {
        logDebug('Maneuvers: Error loading filters from persistent settings:', error);
        if (selectedGrades().length === 0) {
          setSelectedGrades([1]);
          setSelectedGradesManeuvers(['1']);
          logDebug('Maneuvers: Set default grade filter to [1] (>1) after error');
        }
      }
    } else {
      if (selectedGrades().length === 0) {
        setSelectedGrades([1]);
        setSelectedGradesManeuvers(['1']);
        logDebug('Maneuvers: Set default grade filter to [1] (>1) (no user)');
      }
    }
  };

  onMount(async () => {
    // Teardown holders + onCleanup are registered synchronously, BEFORE the awaits below. Solid
    // only has an owner synchronously, so an onCleanup placed next to the resources (created after
    // those awaits) silently never runs — and the disposer this function used to `return` was
    // discarded outright, because Solid ignores onMount return values. The result was one leaked
    // scaling teardown, two ResizeObservers, a MutationObserver, a 100 ms interval and two window
    // listeners per mount.
    let cleanupScaling: (() => void) | null = null;
    let resizeObserver: ResizeObserver | null = null;
    let mapObserver: ResizeObserver | null = null;
    let sidebarObserver: MutationObserver | null = null;
    let sidebarCheckInterval: ReturnType<typeof setInterval> | null = null;
    let handleResize: (() => void) | null = null;
    let handleKeyDown: ((event: KeyboardEvent) => void) | null = null;

    onCleanup(() => {
      cleanupScaling?.();
      resizeObserver?.disconnect();
      mapObserver?.disconnect();
      sidebarObserver?.disconnect();
      if (sidebarCheckInterval) clearInterval(sidebarCheckInterval);
      if (handleResize) window.removeEventListener('resize', handleResize);
      if (handleKeyDown) window.removeEventListener('keydown', handleKeyDown);
    });
    resetManeuverTableSort();
    // A share link already names the baseline, TWS, maneuver type, view and filters. Writing the
    // page defaults after it lands is what dropped them.
    const restored = willRestoreShare(MANEUVERS_PAGE_KEY) ? await shareRestoreReady(MANEUVERS_PAGE_KEY) : false;
    // A link that names a baseline (or "No baseline") must not be replaced by this viewer's saved one.
    // Older links omit the field, so those still restore it.
    const sharedBaseline = consumeShareSpecifiedBaseline(MANEUVERS_PAGE_KEY);
    if (!restored || !sharedBaseline) {
      // Before the first chart draw, so a saved baseline is on the scatter (and on map/table once a TWS bin is chosen).
      await baselineDisplay.restoreSavedBaseline();
    }
    await logPageLoad('Maneuvers.tsx', 'Maneuvers Analysis Report');
    if (!restored) {
      setEventType("TACK")
      setView("MAP")
      setTws("ALL")
      // Set TACK as default color when selectedSourceId > 0
      if (selectedSourceId() > 0) {
        setColor("TACK");
      }
    }
    const className = selectedClassName();
    const projectId = selectedProjectId();

    // Resolve dataset date for project-object lookup
    let dateForFilters: string | null = null;
    const currentDatasetId = typeof selectedDatasetId === 'function' ? selectedDatasetId() : 0;
    if (currentDatasetId > 0 && className && projectId) {
      try {
        const response = await getData(
          `${apiEndpoints.app.datasets}/info?class_name=${encodeURIComponent(className)}&project_id=${encodeURIComponent(projectId)}&dataset_id=${encodeURIComponent(currentDatasetId)}`
        );
        if (response.success && response.data?.date) {
          let dateStr = response.data.date;
          if (dateStr.length === 8 && !dateStr.includes('-')) {
            dateStr = `${dateStr.substring(0, 4)}-${dateStr.substring(4, 6)}-${dateStr.substring(6, 8)}`;
          }
          dateForFilters = dateStr;
        }
      } catch (_) { /* empty */ }
    }

    // Load filters: project default first (by dataset date then 1970-01-01), then user persistent.
    // Skipped for a share: those defaults are this viewer's, and the link already carried the filters.
    if (!restored) {
      const projectFilters = (className && projectId) ? await getProjectManeuverFilters(className, projectId, dateForFilters ?? undefined) : null;
      if (projectFilters === null) {
        await loadFiltersFromPersistentSettings();
      }

      // Set in-report defaults first (when none are set)
      if (!selectedGrades().length) {
        setSelectedGrades([1]);
        setSelectedGradesManeuvers(['1']);
      }

      // Apply project filters on top (overrides defaults; empty array means "no filter")
      if (projectFilters !== null) {
        if (projectFilters.grades !== undefined) {
          const grades = Array.isArray(projectFilters.grades) ? projectFilters.grades : [];
          setSelectedGrades(grades);
          setSelectedGradesManeuvers(grades.map(g => String(g)));
          logDebug('Maneuvers: Applied grade filters from project default', projectFilters.grades);
        }
        if (projectFilters.states !== undefined) {
          // A first-visit seed, not an override: the project holds one flat list for every maneuver
          // type, so it yields to this user's own per-type chips. See applyProjectDefaultStates.
          await applyProjectDefaultStates(projectFilters.states);
          logDebug('Maneuvers: Offered state filters from project default', projectFilters.states);
        }
      }
    }
    await refreshManeuverStateOptions();
    setMapClickGradeReady(true);
    await fetchTableData();
    
    // Set up dynamic scaling for media-container using the global utility
    cleanupScaling = setupMediaContainerScaling({
      logPrefix: 'Maneuvers'
    });
    
    // Set initial heights
    updateContainerHeights();
    
    // Update heights on container resize (scaling is handled by setupMediaContainerScaling)
    resizeObserver = new ResizeObserver(() => {
      updateContainerHeights();
    });
    
    // Observe map element to sync table height when map resizes
    mapObserver = new ResizeObserver(() => {
      if (view() === 'MAP') {
        syncTableHeightWithMap();
      }
    });
    
    const mediaContainer = document.getElementById('media-container');
    if (mediaContainer) {
      // Observe the appropriate reference container (split panel or parent)
      const splitPanel = mediaContainer.closest('.split-panel');
      const mainContent = mediaContainer.closest('#main-content');
      const reference = splitPanel || mainContent || mediaContainer.parentElement;
      if (reference) {
        resizeObserver.observe(reference);
      }
    }
    
    // Observe map element for height changes
    const mapElement = document.querySelector('#maneuver-map');
    if (mapElement) {
      mapObserver.observe(mapElement);
    }
    
    // Also observe map area container
    const mapArea = document.getElementById('map-area');
    if (mapArea) {
      mapObserver.observe(mapArea);
    }
    
    // Observe sidebar for collapse/expand changes
    sidebarObserver = observeSidebar();
    
    // Poll for sidebar changes (since same-tab localStorage changes don't trigger storage event)
    sidebarCheckInterval = setInterval(() => {
      const sidebar = document.querySelector('.sidebar');
      if (sidebar) {
        const isCollapsed = sidebar.classList.contains('collapsed');
        const wasCollapsed = localStorage.getItem('_sidebarWasCollapsed') === 'true';
        if (isCollapsed !== wasCollapsed) {
          localStorage.setItem('_sidebarWasCollapsed', isCollapsed.toString());
          updateContainerHeights();
        }
      }
    }, 100);
    
    // Also listen to window resize for when the main window changes size
    handleResize = () => {
      updateContainerHeights();
    };
    window.addEventListener('resize', handleResize);
    
    // Keyboard shortcuts: 'x' to clear selection, 'c' to cut selection
    handleKeyDown = (event: KeyboardEvent) => {
      // Only handle shortcuts if not typing in an input/textarea
      const target = event.target as HTMLElement | null;
      if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA' || target.isContentEditable)) {
        return;
      }
      
      // Check if ManeuverSettings modal is visible
      const maneuverSettingsModal = document.querySelector('.pagesettings-modal');
      if (maneuverSettingsModal) {
        // Modal is visible, don't handle keyboard shortcuts
        return;
      }
      
      // 'x' key: Clear selection
      if (event.key === 'x' || event.key === 'X') {
        event.preventDefault();
        logDebug('Maneuvers: Clearing selection (x key pressed)');
        clearSelection();
        // Defer chart update to next frame for better responsiveness
        requestAnimationFrame(() => {
          setTriggerUpdate(true);
        });
        return;
      }
      
      // 'h' key: Hide selected events (move to hidden list, then clear selection)
      if (event.key === 'h' || event.key === 'H') {
        event.preventDefault();
        const currentSelected = selectedEvents();
        if (currentSelected && currentSelected.length > 0) {
          logDebug('Maneuvers: Hiding selection (h key pressed)');
          hideSelectedEvents();
          requestAnimationFrame(() => {
            setTriggerUpdate(true);
          });
        }
        return;
      }

      // 'c' key: Cut selection (move selectedEvents to cutEvents)
      if (event.key === 'c' || event.key === 'C') {
        event.preventDefault();
        const currentSelected = selectedEvents();
        if (currentSelected && currentSelected.length > 0) {
          logDebug('Maneuvers: Cutting selection (c key pressed)');
          cutSelection();
          // Defer chart update to next frame for better responsiveness
          requestAnimationFrame(() => {
            setTriggerUpdate(true);
          });
        }
        return;
      }

      if (shouldBlockGradeLikeHotkeysForPerformanceSelections(event.key)) return;

      // Grade hotkeys (0-5): set GRADE for selected events.
      const selected = selectedEvents();
      const gradeKey = ['0', '1', '2', '3', '4', '5'].includes(event.key);
      if (selected && selected.length > 0 && gradeKey) {
        const currentUser = user();
        if (!currentUser) return;
        if (!currentUser.is_super_user) {
          const userPermissions = currentUser.permissions;
          let isReader = false;
          if (typeof userPermissions === 'string') {
            isReader = userPermissions === 'reader';
          } else if (typeof userPermissions === 'object' && userPermissions !== null) {
            const permissionValues = Object.values(userPermissions);
            isReader = permissionValues.length > 0 && permissionValues.every((p: string) => p === 'reader');
          }
          if (isReader) return;
        }
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        const message = `Are you sure you want to update GRADE to ${parseInt(event.key, 10)} for ${selected.length} selected event(s)?\n\nThis action will modify the data and cannot be easily undone.`;
        const confirmed = window.confirm(message);
        if (!confirmed) return;
        setTimeout(() => {
          const update = performGradeUpdate(parseInt(event.key, 10), selected);
          update.catch((err: unknown) => {
            logError('Error in maneuver hotkey update:', err);
          });
        }, 0);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    
    // Teardown is registered by the onCleanup at the top of this onMount (see the note there).
  });

  // Check if maneuvers exist
  const hasManeuvers = createMemo(() => {
    const maneuversData = maneuvers();
    return maneuversData && Array.isArray(maneuversData) && maneuversData.length > 0;
  });

  // Check if filtered maneuvers exist (after grade/TWS filtering)
  const hasFilteredManeuvers = createMemo(() => {
    const filteredIds = filtered();
    return filteredIds && Array.isArray(filteredIds) && filteredIds.length > 0;
  });

  const maneuverTimelinePoints = createMemo(() => {
    const rows = tabledata();
    if (!rows?.length) return [] as Record<string, unknown>[];
    return mapManeuverRowsToScatterTimeseriesPoints(rows as Record<string, unknown>[]);
  });

  const maneuverTimelineGroups = createMemo(() => {
    const rows = tabledata();
    return maneuverTimelineLegendGroups(rows as Record<string, unknown>[], color());
  });

  // Expensive pass: grouping + colours. Re-runs on data or colour-mode change only.
  const maneuverColorLegendBase = createMemo(() => {
    const rows = tabledata() as Record<string, unknown>[];
    // Touch the sources store so SOURCE colours repaint once the fleet colours load.
    if (String(color() || '').toUpperCase() === 'SOURCE') {
      sourcesStore.sources();
      sourcesStore.isReady();
    }
    return buildManeuverColorLegendItems(rows, color(), {
      eventType: eventType(),
      baseline: { name: maneuverBaselineLabel(), color: maneuverBaselineStore.baselineColor() },
    });
  });

  // Cheap pass: pill rings track the same selection the views highlight.
  const maneuverColorLegendItems = createMemo(() =>
    applyManeuverLegendSelection(maneuverColorLegendBase(), color(), {
      grouped: grouped(),
      selectedGroupKeys: grouped() ? selectedGroupKeys() : undefined,
      selectedEventIds: grouped() ? undefined : selectedEvents(),
    })
  );

  // The legend wraps, so its height changes with the colour mode — measured into the MAP/TABLE
  // height math rather than assumed.
  const [legendRowEl, setLegendRowEl] = createSignal<HTMLDivElement | undefined>(undefined);
  createEffect(() => {
    const el = legendRowEl();
    if (!el) return;
    const ro = new ResizeObserver(() => { updateContainerHeights(); });
    ro.observe(el);
    onCleanup(() => ro.disconnect());
  });

  return (
    <div
      id="media-container"
      class={`maneuvers-page${showTimeline() && hasFilteredManeuvers() ? ' maneuvers-page--with-selection-timeline' : ''}`}
      data-dataset-maneuvers-page
    >
      <div class="container">
        {/* Controls always visible so user can change filters even when no data */}
        <div id="maneuver-controls" class="flex flex-col w-full pl-2 pt-2">
            {/* Top Control Bar: 6 buttons in specific layout */}
            <div class="maneuver-control-bar-row flex w-full h-[50px] items-center gap-x-2">
              {/* Left group: 4 buttons */}
              <div class="flex gap-x-2 items-center flex-shrink-0">
                <div class="self-center">
                  <ManeuverSettings
                    useIconTrigger={true}
                    isHistoryPage={false}
                    raceOptions={raceOptions()}
                    legOptions={legOptions()}
                    gradeOptions={gradeOptions()}
                    stateOptions={stateOptions()}
                    selectedRaces={selectedRaces()}
                    selectedLegs={selectedLegs()}
                    selectedGrades={selectedGrades()}
                    selectedStates={selectedStates()}
                    selectedTrainingRacing={maneuverTrainingRacing()}
                    onTrainingRacingFilterChange={setManeuverTrainingRacing}
                    selectedTackEntry={maneuverTackEntry()}
                    onTackEntryFilterChange={setManeuverTackEntry}
                    setRaceOptions={setRaceOptions}
                    setLegOptions={setLegOptions}
                    setGradeOptions={setGradeOptions}
                    setStateOptions={setStateOptions}
                    toggleRaceFilter={toggleRaceFilter}
                    toggleLegFilter={toggleLegFilter}
                    toggleGradeFilter={toggleGradeFilter}
                    toggleStateFilter={toggleStateFilter}
                    onApplyFilters={() => { fetchTableData(); filterData(); }}
                    includeAllGradeOption={true}
                    componentConfig={{
                      showGrades: true,
                      showRaces: true,
                      showLegs: true,
                      showStates: true,
                      showTackEntry: true,
                    }}
                    showTimeline={() => showTimeline()}
                    onTimelineChange={handleTimelineChange}
                    onSelectBaseline={() => void baselineDisplay.openModal()}
                    baselineName={baselineDisplay.baselineLabel}
                  />
                </div>
                <DropDownButton 
                  options={eventTypes()}
                  defaultText={eventType()}
                  smallLabel="Maneuver"
                  size="big"
                  handleSelection={handleEventType}
                />
                <DropDownButton
                  options={viewOptions()}
                  defaultText={grouped() && view() === 'SCATTER' ? 'BOXES' : view()}
                  smallLabel="View"
                  size="big"
                  handleSelection={handleView}
                />
                <Show when={view() === 'TIME SERIES' || view() === 'MAP' || view() === 'VIDEO'}>
                  <DropDownButton
                    options={phases()}
                    defaultText={phase()}
                    smallLabel="Phase"
                    size="big"
                    handleSelection={handlePhase}
                  />
                </Show>
                {/* Active Filters: right side of left button area */}
                <div class="maneuver-panel-filter-summary">
                  <PerformanceFilterSummary
                    filterGrades={selectedGrades().join(",")}
                    filterState={selectedStates().join(",")}
                    stateOptions={stateOptions()}
                    trainingRacing={maneuverTrainingRacing()}
                    tackEntry={maneuverTackEntry()}
                    gradeAsGreaterThan
                  />
                </div>
              </div>

              {/* Right button area: TWS, Grouped, Data, Color By (unchanged) */}
              <div class="maneuver-control-bar-right flex gap-x-2 items-center ml-auto flex-shrink-0">
                <DropDownButton
                  options={twsoptions()}
                  defaultText={tws()}
                  smallLabel="Tws"
                  size="small"
                  handleSelection={handleTws}
                />
                <DropDownButton
                  options={groupedOptions()}
                  defaultText={groupDisplayMode()}
                  smallLabel="Group"
                  size="small"
                  handleSelection={(val) => { setGroupDisplayMode(val); setTriggerUpdate(true); }}
                />
                <Show when={view() === 'MAP' || view() === 'TIME SERIES' || view() === 'VIDEO' || view() === 'SCATTER'}>
                  <DropDownButton
                    options={colors()}
                    defaultText={color()}
                    smallLabel="Color By"
                    size="big"
                    handleSelection={handleColor}
                  />
                </Show>
              </div>
            </div>
            
            <Show when={showTimeline() && hasFilteredManeuvers()}>
              <div class="maneuver-selection-timeline">
                <ScatterTimeseries
                  key={`maneuver-tl-${maneuverTimelinePoints().length}-${color()}-${tws()}-${eventType()}`}
                  aggregates={maneuverTimelinePoints()}
                  color={color()}
                  groups={maneuverTimelineGroups()}
                  isHistoryPage={false}
                  showUwDw={false}
                  skipSelectedSourcesFilter={true}
                />
              </div>
            </Show>
            {/* Shared color-by legend for all views. Deliberately outside the strip's Show: it
                stays visible when the Timeline toggle is off, so MAP/TABLE keep their legend. */}
            <Show when={hasFilteredManeuvers() && maneuverColorLegendItems().length > 0}>
              <div
                id="maneuver-color-legend-row"
                class="maneuver-color-legend-row"
                ref={setLegendRowEl}
              >
                <ManeuverColorLegend
                  colorMode={color()}
                  items={maneuverColorLegendItems()}
                  onItemClick={handleLegendClick}
                  onBaselineClick={() => void baselineDisplay.openModal()}
                />
              </div>
            </Show>
        </div>
        {/* Content section: empty state when no data, then filter-too-strict or views */}
        <Show when={!hasManeuvers()} fallback={null}>
          <div class="flex flex-col items-center justify-center w-full min-h-[40vh] px-4 text-gray-600">
            <p class="text-lg font-medium">No maneuvers to display</p>
            <p class="text-sm mt-2 text-center max-w-md">
              Ensure this dataset has been processed and maneuver events exist. Try selecting a different maneuver type (e.g. TACK) above—this dataset may only have certain types. Adjust filters and apply to reload.
            </p>
          </div>
        </Show>
        <Show when={hasManeuvers() && !hasFilteredManeuvers()}>
          <div class="flex items-center justify-center h-[calc(100vh-200px)]">
            <div class="text-center">
              <p class="text-xl text-gray-600">No maneuvers found</p>
              <p class="text-sm text-gray-500 mt-1">Filtering criteria may be too strict, check your settings...</p>
            </div>
          </div>
        </Show>
        <Show when={hasFilteredManeuvers()}>
          <Show when={view() === 'MAP'}>
            <div
              id="maneuver-map-layout"
              class="maneuver-map-layout flex w-full"
              classList={{ 'maneuver-map-layout--chart-panel': mapSidePanel() === 'CHART' }}
            >
              <div id="table-area">
                <Show when={!grouped()}>
                  <Show when={mapSidePanel() === 'TABLE'}>
                    <DataTable_Small context={context} />
                    <Show when={selectedEvents().length > 0}>
                      <div class="flex justify-start mt-2">
                        <button
                          class="btn"
                          type="button"
                          onClick={() => setMapSidePanel('CHART')}
                        >
                          Show Timeseries →
                        </button>
                      </div>
                    </Show>
                  </Show>
                  <Show when={mapSidePanel() === 'CHART'}>
                    <div style={{ height: "100%", display: "flex", "flex-direction": "column", "min-height": "0" }}>
                      <div style={{ flex: "0 0 auto", "min-height": "0" }}>
                        <TimeSeries context={context} selectedOnly playhead fitParent />
                      </div>
                      {/* Clears however far the chart overruns the map, then the map caption's 8px gap. */}
                      <div class="flex justify-start" style={{ "flex-shrink": "0", "margin-top": `${MAP_SIDE_PANEL_EXTRA_HEIGHT_PX + MAP_SIDE_PANEL_BUTTON_GAP_PX}px` }}>
                        <button class="btn" type="button" onClick={() => setMapSidePanel('TABLE')}>
                          ← Show Data Table
                        </button>
                      </div>
                    </div>
                  </Show>
                </Show>
                <Show when={grouped()}>
                  <DataTable_SmallGrouped context={context} />
                </Show>
              </div>
              <div id="map-area">
                <Show when={!grouped()}>
                  <Map context={context} onDataUpdate={fetchTableData} />
                </Show>
                <Show when={grouped()}>
                  <MapGrouped context={context} onDataUpdate={fetchTableData} />
                </Show>
              </div>
            </div>
          </Show>
          <Show when={view() === 'VIDEO'}>
            <div id="maneuver-map-layout" class="maneuver-map-layout flex w-full">
              <div id="table-area">
                <Show when={!grouped()}>
                  <Show when={videoSidePanel() === 'TABLE'}>
                    <DataTable_Small context={context} />
                    <Show when={selectedEvents().length > 0}>
                      <div class="flex justify-start mt-2">
                        <button
                          class="btn"
                          type="button"
                          onClick={() => setVideoSidePanel('CHART')}
                        >
                          Show Timeseries →
                        </button>
                      </div>
                    </Show>
                  </Show>
                  <Show when={videoSidePanel() === 'CHART'}>
                    <div style={{ height: "100%", display: "flex", "flex-direction": "column", "min-height": "0" }}>
                      <div style={{ flex: "1 1 auto", "min-height": "0" }}>
                        <TimeSeries context={context} selectedOnly playhead fitParent />
                      </div>
                      <div class="flex justify-start mt-2" style={{ "flex-shrink": "0" }}>
                        <button class="btn" type="button" onClick={() => setVideoSidePanel('TABLE')}>
                          ← Show Data Table
                        </button>
                      </div>
                    </div>
                  </Show>
                </Show>
                <Show when={grouped()}>
                  <DataTable_SmallGrouped context={context} />
                </Show>
              </div>
              <div id="map-area">
                <Video context={context} onDataUpdate={fetchTableData} />
              </div>
            </div>
          </Show>
          <Show when={view() === 'SCATTER'}>
            <div id="scatter-area">
              <Show when={!grouped()}>
                <Scatter context={context} />
              </Show>
              <Show when={grouped()}>
                <ScatterGrouped context={context} />
              </Show>
            </div>
          </Show>
          <Show when={view() === 'TIME SERIES'}>
            <div id="timeseries-area">
              <Show when={!grouped()}>
                <TimeSeries context={context} onDataUpdate={fetchTableData} />
              </Show>
              <Show when={grouped()}>
                <TimeSeriesGrouped context={context} onDataUpdate={fetchTableData} />
              </Show>
            </div>
          </Show>
          <Show when={view() === 'TABLE'}>
            <div id="datatable-big-wrapper">
              <Show when={!grouped()}>
                <DataTable_Big context={context} onDataUpdate={fetchTableData} />
              </Show>
              <Show when={grouped()}>
                <DataTable_BigGrouped context={context} onDataUpdate={fetchTableData} />
              </Show>
            </div>
          </Show>
        </Show>
      </div>
      <BaselineDisplayModal
        show={baselineDisplay.showModal()}
        onClose={baselineDisplay.closeModal}
        title="Select Baseline"
        baselines={baselineDisplay.availableBaselines()}
        selectedKey={baselineDisplay.selectedKey()}
        onSelectedKeyChange={baselineDisplay.setSelectedKey}
        configs={baselineDisplay.modalConfigs()}
        selectedConfig={baselineDisplay.modalConfig()}
        onSelectedConfigChange={baselineDisplay.setModalConfig}
        baselineColor={baselineDisplay.modalColor()}
        onBaselineColorChange={baselineDisplay.setModalColor}
        loading={baselineDisplay.loading()}
        loadingAction={baselineDisplay.loadingAction()}
        error={baselineDisplay.error()}
        onConfirm={() => void baselineDisplay.handleConfirm()}
        onRemove={() => void baselineDisplay.handleRemove()}
      />
    </div>
  );
}
